import { Box, HStack, RadioCard, Text } from '@chakra-ui/react';
import type { ComponentType, SVGProps } from 'react';
import { AiDraftBadge } from '@/pages/collabs/brief-templates/_shared/BriefBadges';

interface CollabBriefOptionCardProps {
  value: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  meta: string;
  isAiDraft?: boolean;
  /** Listed but not pickable: the meta line says why. */
  disabled?: boolean;
}

/**
 * One brief choice in the wizard's template step: a horizontal radio card with
 * a small icon tile, the template name (plus the "AI draft" badge when the
 * library template came from generation), and a one-line meta summary.
 */
export const CollabBriefOptionCard = ({
  value,
  icon: Icon,
  title,
  meta,
  isAiDraft = false,
  disabled = false,
}: CollabBriefOptionCardProps) => (
  <RadioCard.Item
    value={value}
    disabled={disabled}
    p="0"
    bg="transparent"
    borderWidth="0"
    _checked={{ borderWidth: '0', boxShadow: 'none' }}
  >
    <RadioCard.ItemHiddenInput />
    <RadioCard.ItemControl
      position="relative"
      display="flex"
      alignItems="center"
      gap="3"
      w="full"
      p="12px 14px"
      borderWidth="1px"
      borderColor="border"
      rounded="lg"
      bg="panelBg"
      _checked={{ borderColor: 'primary.900', bg: 'softCream' }}
      _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
    >
      <Box
        boxSize="34px"
        rounded="lg"
        bg="whiteSmoke"
        color="primary.500"
        borderWidth="1px"
        borderColor="border"
        display="grid"
        placeItems="center"
        flex="none"
      >
        <Icon width={16} height={16} />
      </Box>
      <Box flex="1" minW="0">
        <HStack gap="2">
          <RadioCard.ItemText
            fontFamily="heading"
            fontSize="13.5px"
            fontWeight="semibold"
            color="dark"
            lineClamp={1}
          >
            {title}
          </RadioCard.ItemText>
          {isAiDraft && <AiDraftBadge />}
        </HStack>
        <Text fontSize="11.5px" color="fg.muted" lineClamp={1} mt="0.5">
          {meta}
        </Text>
      </Box>
      <RadioCard.ItemIndicator flex="none" />
    </RadioCard.ItemControl>
  </RadioCard.Item>
);
